import { applicationState, SignedInState } from 'shared/store/applicationState'
import { useLocation } from 'react-router-dom'
import userManager from 'overrides/user-manager';
import { waitForUserLoad } from 'overrides/api';
import { getUserProfile } from "shared/services/userApi";
import { UserProfile } from 'shared/models/site/user-profile';


export async function loadUserFromStorage() {
    try {
        applicationState.setSignedInState(SignedInState.Loading);
        let user = await userManager.getUser();

        if (user?.expires_in as number < userManager.accessTokenExpiringNotificationTime || !user) {
            try {
                console.log('trying to sign in silently');
                user = await userManager.signinSilent();
            }
            catch (e: any) {
                // OIDC errors that mean silent SSO cannot proceed and the user must
                // authenticate/consent interactively. This includes the Entra
                // device-registration / consent prompt, which breaks the silent iframe
                // flow with 'interaction_required' / 'consent_required' (not 'login_required').
                const oidcError = e?.error ?? e?.t0?.error;
                const interactiveRequired = [
                    'login_required',
                    'interaction_required',
                    'consent_required',
                    'account_selection_required',
                ].includes(oidcError);

                if (interactiveRequired) {
                    if (window?.location?.pathname) {
                        sessionStorage.setItem("pre-login-route", window.location.pathname);
                    }
                    signinRedirect()
                    return;
                }
                throw (e);
            }
        }

        if (!user) {
            applicationState.setSignedInState(SignedInState.NotSignedIn);
            throw ('User not found')
        }

        applicationState.setUser(user);
        applicationState.setSignedInState(SignedInState.SignedIn);
    }
    catch (e) {
        // Never leave the app stuck in the Loading state. If we do, waitForUserLoad()
        // keeps timing out ("Timeout waiting for expected user loading state") and
        // loadUserProfile() retries forever. Resolving to NotSignedIn lets the
        // ProtectedRoute redirect to interactive sign-in instead of spinning.
        if (applicationState.getSignedInState() === SignedInState.Loading) {
            applicationState.setSignedInState(SignedInState.NotSignedIn);
        }
        console.error(`User not found: ${ e }`);
    }
}

export async function loadUserProfile() {
    try {
        await waitForUserLoad();

        var profile = undefined as undefined | UserProfile;

        let user = applicationState.getUser();

        if (user !== undefined && (user.roles === undefined || user.roles.length < 1)) {
            profile = {
                roles: [] as any[]
            } as UserProfile;
            applicationState.setProfile(profile);
            return;
        }

        profile = await getUserProfile();

        if (profile) {
            applicationState.setProfile(profile);
        }
    }
    catch (e) {
        console.error(`User profile not loaded: ${ e }`);
    }
    finally {
        if (!profile) {
            // Profile is required, keep trying
            setTimeout(() => { loadUserProfile(); }, 1000);
            console.error('User Profile not found, retrying..');
        }
    }

}

export async function loadAndGetUserProfile(): Promise<UserProfile> {
    return new Promise(async (resolve) => {
        const tryGetProfile = async () => {
            try {
                // Wait for the user object to be loaded first
                await waitForUserLoad();

                const profile = await getUserProfile();

                if (profile) {
                    applicationState.setProfile(profile);
                    resolve(profile);
                } else {
                    console.error('User Profile not found, retrying in 1s...');
                    setTimeout(tryGetProfile, 1000);
                }
            } catch (e) {
                console.error(`Failed to load user profile: ${e}, retrying in 1s...`);
                setTimeout(tryGetProfile, 1000);
            }
        };

        await tryGetProfile();
    });
}

export function signinRedirectCallback() {
    return userManager.signinRedirectCallback()
        .then(user => {
            applicationState.setUser(user || undefined);
            return user;
        })
        .catch(e => {
            console.log(e)
        });
}

export function signoutRedirect() {
    userManager.clearStaleState();
    userManager.removeUser();
    return userManager.signoutRedirect();
}

export function signoutRedirectCallback() {
    userManager.clearStaleState();
    userManager.removeUser();
    return userManager.signoutRedirectCallback();
}

export function signinSilentCallback() {
    return userManager.signinSilentCallback();
}

export const signinRedirect = () => {
    try {
        const location = useLocation();
        if (typeof (location?.pathname) === 'string') {
            sessionStorage.setItem("pre-login-route", location.pathname);
        }
    }
    catch { }

    userManager.signinRedirect();
}
export default userManager;
